import { Image, Text } from "@mantine/core";
import { useState } from "react";

import { useThumbFirst } from "../../ui/thumb-first";

export interface OutputMediaProps {
  kind: "image" | "video";
  /** The signed URL the API returned with the job; rendered as is, never stored (G5). */
  src: string;
  alt: string;
  /** Reads the job again, for fresh URLs: called once, on the first load that fails. */
  onExpired: () => void;
  /** A tile's lighter picture of an image: the output's 512 px thumbnail (issue 828, item 12);
   *  none, or not made yet, shows `src`. */
  thumb?: string | null | undefined;
  /** A video's still until it plays (issue 828, item 12): with one, nothing of the video loads
   *  before it is played; without, its metadata loads for its first frame. */
  poster?: string | null | undefined;
}

/**
 * A delivered output, contained in the box it is given (the detail card's media pane). Its URL is signed for a limited time, so a load
 * that fails (most often an expired URL on a page left open) reads the job again once; a URL
 * that fails after that shows a line instead of a broken preview.
 */
export function OutputMedia({ kind, src, alt, onExpired, thumb = null, poster = null }: OutputMediaProps) {
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const [readAgain, setReadAgain] = useState(false);
  const failed = () => {
    setFailedSrc(src);
    if (readAgain) return;
    setReadAgain(true);
    onExpired();
  };
  // An image's thumbnail first; one that fails falls back to the file, and only the file's
  // failure reads the job again (issue 828, item 12).
  const picture = useThumbFirst(kind === "image" ? thumb : null, src, failed);
  // Until the job is read again with a new URL; for good once that one fails too.
  if (failedSrc === src) {
    return (
      <Text size="sm" c="var(--cai-color-text-muted)">
        The preview did not load.
      </Text>
    );
  }
  if (kind === "image") {
    return (
      <Image src={picture.src} alt={alt} fit="contain" w="auto" maw="100%" mah="100%" onError={picture.onError} />
    );
  }
  return (
    // A generated video has no captions to offer, and plays with its own sound, as live's (spec
    // web/player-sound).
    // oxlint-disable-next-line jsx-a11y/media-has-caption
    <video
      src={src}
      {...(poster === null ? {} : { poster })}
      controls
      preload={poster === null ? "metadata" : "none"}
      aria-label={alt}
      style={{ maxWidth: "100%", maxHeight: "100%" }}
      onError={failed}
    />
  );
}
